import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Container, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import OrganisationService from '../../../shared/services/organisation/organisation.service';
import { IOrganisationDetails } from '../../../shared/services/organisation/organisation.model';
import DashboardService from '../../../shared/services/dashboard/dashboard.service';
import { IDashboard } from '../../../shared/services/dashboard/dashboard.model';
import {
  ANALYTICS_HOME_TABS, analyticsTabHomeValue, customDashboardHomeValue,
} from '../../../shared/utils/home-view';

const userDetailsService = new UserDetailsService();
const organisationService = new OrganisationService();
const dashboardService = new DashboardService();

export const HomePageSettingsPage = () => {
  const userDetailsContext = useContext(UserDetailsContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const userDetails = userDetailsContext.userDetails;

  const [dashboards, setDashboards] = useState<IDashboard[]>([]);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);

  const isAdmin = !!userDetails?.roles?.includes('admin');

  useEffect(() => {
    dashboardService.getAll().then(setDashboards).catch((err) => console.log(err));
  }, []);

  // The pickable landing views: the standard dashboard, each default analytics tab, and each
  // custom dashboard the user can see.
  const options = useMemo(() => ([
    { value: '', label: 'Default dashboard' },
    ...ANALYTICS_HOME_TABS.map((t) => ({ value: analyticsTabHomeValue(t.key), label: t.label })),
    ...dashboards.map((d) => ({ value: customDashboardHomeValue(d.id), label: `Dashboard: ${d.name}` })),
  ]), [dashboards]);

  const personalValue = userDetails?.homeView || '';
  const orgValue = userDetails?.organisation?.defaultHomeView || '';

  const handlePersonalChange = async (value: string) => {
    try {
      setSavingPersonal(true);
      await userDetailsService.updateHomeView(value || null);
      await userDetailsContext.forceUpdateUserDetails();
      userNotificationsContext.openSnackbar({ open: true, message: 'Home page updated' });
    } catch (e: any) {
      userNotificationsContext.openSnackbar({ open: true, message: e?.response?.data?.message || 'Something went wrong', backgroundColor: '#70787A' });
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleOrgChange = async (value: string) => {
    if (!userDetails?.organisation) return;
    try {
      setSavingOrg(true);
      // organisation/update validates against a strict Joi schema (name + currencyCode required),
      // so send only those known fields plus the new default.
      await organisationService.editOrganisations({
        name: userDetails.organisation.name,
        currencyCode: userDetails.organisation.currencyCode,
        defaultHomeView: value || null,
      } as IOrganisationDetails);
      await userDetailsContext.forceUpdateUserDetails();
      userNotificationsContext.openSnackbar({ open: true, message: 'Organisation default updated' });
    } catch (e: any) {
      userNotificationsContext.openSnackbar({ open: true, message: e?.response?.data?.message || 'Something went wrong', backgroundColor: '#70787A' });
    } finally {
      setSavingOrg(false);
    }
  };

  return (
    <PageOuterContainer>
      <PageHeading title="Home Page" hasTabs={false} />
      <Container maxWidth={false} sx={{ px: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, mb: 3 }}>
          Choose which page you land on when you sign in. Pick the standard dashboard, one of the
          analytics tabs, or one of your custom dashboards.
        </Typography>

        <Box sx={{ maxWidth: 420, mb: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="home-view-label">Your home page</InputLabel>
            <Select
              labelId="home-view-label"
              label="Your home page"
              value={personalValue}
              disabled={savingPersonal}
              onChange={(e) => handlePersonalChange(e.target.value)}
            >
              {options.map((o) => <MenuItem key={o.value || 'default'} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {isAdmin && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}><b>Organisation default</b></Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, mb: 2 }}>
              The landing page for everyone in your organisation who has not chosen their own.
            </Typography>
            <Box sx={{ maxWidth: 420 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="org-home-view-label">Organisation home page</InputLabel>
                <Select
                  labelId="org-home-view-label"
                  label="Organisation home page"
                  value={orgValue}
                  disabled={savingOrg}
                  onChange={(e) => handleOrgChange(e.target.value)}
                >
                  {options.map((o) => <MenuItem key={o.value || 'default'} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </>
        )}
      </Container>
    </PageOuterContainer>
  );
};
